import { Box, Flex, Text, Center, Icon, VStack, useMediaQuery } from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { Filter } from '../../components/Objects/Filter/Filter';
import { TopBar } from '../../components/Objects/TopBar/TopBar';
import {
  fetchAllObjects,
  getDefaultObjectsParams,
  getInitialObjectsParamas,
  getObjectFilterParamsWithoutEmptyProperties,
} from '../../services/Objects/ObjectsService';
import { StringOrNumber } from '@chakra-ui/utils';
import { ObjectsParams } from '../../types/ParamsTypes';
import { ObjectsResponse } from '../../types/ObjectsTypes';
import {
  loadStateFromLocalStorage,
  saveStateToLocalStorage,
} from '../../services/LocalStorage/serveLocalStorageService';
import { ObjectsList } from '../../components/Objects/ObjectsList';
import { ObjectsListPagination } from '../../components/Objects/ObjectsListPagination';
import { ModalView } from '../../components/Objects/Filter/ModalView/ModalView';
import { NavBar } from '../../components/Objects/NavBar/NavBar';
import { HiOutlineSearch } from 'react-icons/all';
import { ExtendedDetailsRoute } from '../../types/RoutesTypes';
import { useHistory, useParams } from 'react-router-dom';
import { APP_VIEW, getAppView } from '../../services/VersionManagement/VersionManagementService';

export interface ObjectsViewProps {
  handleLoadingSpinner: (value: boolean) => void;
  isLoading: boolean;
}

export const ObjectsView = (props: ObjectsViewProps): JSX.Element => {
  const { handleLoadingSpinner, isLoading } = props;
  const history = useHistory();
  const routeParams: ExtendedDetailsRoute = useParams();
  const [objects, setObjects] = useState<ObjectsResponse>();
  const [showResults, setShowResults] = useState<boolean>(
    document.querySelector('[data-api-key]')?.getAttribute('data-show-initial-results') === 'true',
  );
  const [isReloading, setIsReloading] = useState<boolean>(true);
  const [objectsParams, setObjectsParams] = useState<ObjectsParams>(
    getInitialObjectsParamas(routeParams),
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedCommonFeatures, setSelectedCommonFeatures] = useState<StringOrNumber[]>([]);
  const [selectedObjectTypeFeatures, setSelectedObjectTypeFeatures] = useState<StringOrNumber[]>(
    [],
  );
  const [appView] = useState<APP_VIEW>(getAppView());
  const [isTabletOrMobile] = useMediaQuery('(max-width: 767px)');

  const SUM_OBJECTS = objects ? objects?.data.result.totalQuantity : 10;
  const LIMIT_OBJECTS = 10;
  const numberOfPages = Math.ceil(SUM_OBJECTS / LIMIT_OBJECTS);

  const handleFilterChange = useCallback(
    (updatedObjectsParams: ObjectsParams) => {
      if (!showResults) {
        handleLoadingSpinner(true);
        setShowResults(true);
      }

      setObjectsParams({
        ...updatedObjectsParams,
        agesOfChildren: updatedObjectsParams.agesOfChildren,
      });
    },
    [showResults],
  );

  const handleFilterReset = useCallback(() => {
    history.push('/');
    setObjectsParams(getDefaultObjectsParams());
    setSelectedCommonFeatures([]);
    setSelectedObjectTypeFeatures([]);
  }, []);

  const handleObjectTypeFeatureChange = useCallback(
    (values: StringOrNumber[]) => {
      setSelectedObjectTypeFeatures(values);
      setObjectsParams({
        ...objectsParams,
        features: [...values, ...selectedCommonFeatures] as string[],
      });
    },
    [objectsParams, selectedCommonFeatures],
  );

  const handleCommonFeatureChange = useCallback(
    (values: StringOrNumber[]) => {
      setSelectedCommonFeatures(values);
      setObjectsParams({
        ...objectsParams,
        features: [...values, ...selectedObjectTypeFeatures] as string[],
      });
    },
    [objectsParams, selectedObjectTypeFeatures],
  );

  const handlePageChange = (number: number): void => {
    if (number >= 1 && number <= numberOfPages) {
      handleLoadingSpinner(true);
      setCurrentPage(number);
    }
  };

  useEffect(() => {
    (async function () {
      const objectsParamsForRequest = getObjectFilterParamsWithoutEmptyProperties(objectsParams);
      setIsReloading(true);
      const offset = currentPage * LIMIT_OBJECTS - LIMIT_OBJECTS;

      const objects = await fetchAllObjects({
        ...objectsParamsForRequest,
        limit: LIMIT_OBJECTS,
        offset: offset,
        sorting: objectsParamsForRequest.sorting,
      });
      setIsReloading(false);
      handleLoadingSpinner(false);
      setObjects(objects.data);
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    })();
  }, [handleLoadingSpinner, objectsParams, currentPage]);

  const HOLIDAY_BOOKING_HOME_BOOKMARK = 'HOLIDAY_BOOKING_HOME_BOOKMARK';

  const bookmarkFromLocalStorage: ExtendedDetailsRoute[] = loadStateFromLocalStorage(
    HOLIDAY_BOOKING_HOME_BOOKMARK,
  );

  const defaultBookmarkState = bookmarkFromLocalStorage ? bookmarkFromLocalStorage : [];

  const [bookmarks, setBookmarks] = useState(defaultBookmarkState);

  const handleBookmarkChange = useCallback(
    (value: ExtendedDetailsRoute, action?: string) => {
      let newValues = [...bookmarks];

      if (action === 'ADD') {
        newValues.push(value);
      } else {
        newValues = newValues.filter(el => el.id !== value.id);
      }

      setBookmarks(newValues);
      saveStateToLocalStorage(newValues, HOLIDAY_BOOKING_HOME_BOOKMARK);
    },
    [bookmarks],
  );

  return (
    <>
      {!isLoading && (
        <Flex flexDirection={'column'} alignItems={'flex-start'} gridGap={4}>
          {objects && showResults && (
            <>
            <NavBar handleLoadingSpinner={handleLoadingSpinner} bookmarks={bookmarks.length} />
            {appView === APP_VIEW.MIN &&
              <Filter
                setShowResults={setShowResults}
                showResults={showResults}
                data={objects.data.result}
                handleFilterChange={handleFilterChange}
                handleObjectTypeFeatureChange={handleObjectTypeFeatureChange}
                handleCommonFeatureChange={handleCommonFeatureChange}
                handleFilterReset={handleFilterReset}
                objectsParams={objectsParams}
                selectedCommonFeatures={selectedCommonFeatures}
                selectedObjectTypeFeatures={selectedObjectTypeFeatures}
                handleLoadingSpinner={handleLoadingSpinner}
              />
            }
          </>
          )}
          <Flex gridGap={8} width={'100%'}>
            {objects && !showResults && appView !== APP_VIEW.MIN && (
              <Filter
                setShowResults={setShowResults}
                showResults={showResults}
                data={objects.data.result}
                handleFilterChange={handleFilterChange}
                handleObjectTypeFeatureChange={handleObjectTypeFeatureChange}
                handleCommonFeatureChange={handleCommonFeatureChange}
                handleFilterReset={handleFilterReset}
                objectsParams={objectsParams}
                selectedCommonFeatures={selectedCommonFeatures}
                selectedObjectTypeFeatures={selectedObjectTypeFeatures}
                handleLoadingSpinner={handleLoadingSpinner}
              />
            )}
            {objects && showResults && (
              <>
                {appView !== APP_VIEW.MIN &&
                  <Box
                    width={320}
                    display={{ base: 'none', md: 'block' }}
                    borderRadius={4}
                    borderColor={'gray.300'}
                    borderWidth={1}>
                    <Filter
                      setShowResults={setShowResults}
                      showResults={showResults}
                      data={objects.data.result}
                      handleFilterChange={handleFilterChange}
                      handleObjectTypeFeatureChange={handleObjectTypeFeatureChange}
                      handleCommonFeatureChange={handleCommonFeatureChange}
                      handleFilterReset={handleFilterReset}
                      objectsParams={objectsParams}
                      selectedCommonFeatures={selectedCommonFeatures}
                      selectedObjectTypeFeatures={selectedObjectTypeFeatures}
                      handleLoadingSpinner={handleLoadingSpinner}
                    />
                  </Box>
                }

                <Flex
                  flexDirection={'column'}
                  flex={1}
                  width={{ base: '100%', md: '60%', xl: 'unset' }}
                  gridGap={4}>
                  {appView !== APP_VIEW.MIN &&
                  <>
                    <Box display={{ base: 'block', md: 'none' }} w={'100%'}>
                      <ModalView numberOfBookings={objects.data.result.totalQuantity}>
                        <Filter
                          setShowResults={setShowResults}
                          showResults={showResults}
                          data={objects.data.result}
                          handleFilterChange={handleFilterChange}
                          handleObjectTypeFeatureChange={handleObjectTypeFeatureChange}
                          handleCommonFeatureChange={handleCommonFeatureChange}
                          handleFilterReset={handleFilterReset}
                          objectsParams={objectsParams}
                          selectedCommonFeatures={selectedCommonFeatures}
                          selectedObjectTypeFeatures={selectedObjectTypeFeatures}
                          handleLoadingSpinner={handleLoadingSpinner}
                          />
                      </ModalView>
                    </Box>

                    <TopBar
                      objectsParams={objectsParams}
                      onFilterChange={handleFilterChange}
                      numberOfBookings={objects.data.result.totalQuantity}
                      isReloading={isReloading}
                      />
                      </>
                  }
                  {objects.data.result.totalQuantity > 0 && (
                    <>
                      <ObjectsList
                        objectsParams={objectsParams}
                        objects={objects.data.result.bookings as any[]}
                        bookmarks={bookmarks}
                        handleBookmarkChange={handleBookmarkChange}
                      />
                      <ObjectsListPagination
                        numberOfPages={numberOfPages}
                        handlePageChange={handlePageChange}
                        currentPage={currentPage}
                      />
                    </>
                  )}
                  {objects.data.result.totalQuantity === 0 && (
                    <Flex flex={1}>
                      <Center textAlign={'center'} width={'100%'}>
                        <VStack>
                          <Icon as={HiOutlineSearch} fontSize={'4xl'} color={'green.500'} />
                          <Text width={'100%'}>
                            Es wurden keine Ergebnisse gefunden. Bitte ändern Sie die Suchkriterien.
                          </Text>
                        </VStack>
                      </Center>
                    </Flex>
                  )}
                </Flex>
              </>
            )}
          </Flex>
        </Flex>
      )}
    </>
  );
};
